import React, { type ComponentType, type HTMLAttributes, type SVGAttributes } from 'react';
import { IconCross } from '@/app/common/icons/libraryIcons';
import styled from 'styled-components';
import { useToggle } from 'react-use';

const MessageContainer = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: ${({ theme }) => theme.commons.font.sizes.base};
  display: flex;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: ${({ theme }) => theme.commons.colors.primary};

  & svg,
  & [data-library-icon] {
    width: 24px;
    height: 24px;
  }
`;

const ContentContainer = styled.div`
  flex: 1 0 0;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  margin-left: 0.5rem;
  font-size: ${({ theme }) => theme.commons.font.sizes.medium};
  padding: 0;
  color: ${({ theme }) => theme.commons.colors.gray200};
  background: none;
  border: none;
`;
export type MessageProps = {
  /** any valid react children. Use predefined children such as`Message.Title` for consisten styling. */
  children: React.ReactNode;
  /** will show a **close** icon if `true` that hides the message when clicked. Defaults to `false` */
  isDismissable?: boolean;
  /** optional Icon, shown on the left in the first text line */
  icon?: ComponentType<React.PropsWithChildren<React.PropsWithChildren<SVGAttributes<SVGElement>>>>;
} & HTMLAttributes<HTMLDivElement>;

/**
 * A message displays information that explains nearby content.
 *
 * It can
 *   - contain an explanatory Icon,
 *   - be dismissed &
 *   - display "rich" content.
 */
export default function Message(props: MessageProps) {
  const { children, icon: Icon, isDismissable, ...rest } = props;
  const [isVisible, hide] = useToggle(true);
  if (!isVisible) return null;
  return (
    <MessageContainer {...rest}>
      {Icon && (
        <IconContainer>
          <Icon />
        </IconContainer>
      )}
      <ContentContainer>{children}</ContentContainer>
      {isDismissable && (
        <CloseButton aria-label="close Message" onClick={hide}>
          <IconCross />
        </CloseButton>
      )}
    </MessageContainer>
  );
}

const Title = styled.div`
  font-size: ${({ theme }) => theme.commons.font.sizes.medium};
  font-weight: ${({ theme }) => theme.commons.font.weights.semiBold};
  margin-bottom: 0.5rem;
`;

/**
 * Default Title Component for usage in `Message`. Such as
 * ```
 * <Message>
 *   <Message.Title>This is the title</Message.Title>
 *    further content
 * </Message>
 * ```
 */
Message.Title = Title;
